import React, { useMemo, useState } from "react";
import { CheckCircle2, Copy, ExternalLink, Send } from "lucide-react";
import { useToast } from "../../hooks/useToast";
import {
  getProfileCompletion,
  getSampleLeadDismissedStorageKey,
} from "../../utils/tradieWorkspace";
import HowItWorksPanel from "./HowItWorksPanel";

export default function TradieOverview({
  profile,
  businessId,
  requests = [],
  onGoToToday,
  onGoToPipeline,
  onGoToSettings,
}) {
  const { showToast } = useToast();
  const completion = getProfileCompletion(profile);
  const [sampleDismissed, setSampleDismissed] = useState(() => {
    try {
      return localStorage.getItem(getSampleLeadDismissedStorageKey(businessId)) === "1";
    } catch {
      return false;
    }
  });

  const link = useMemo(() => {
    if (!businessId) return "";
    const appBase = (process.env.REACT_APP_APP_URL || window.location.origin).replace(/\/$/, "");
    return `${appBase}?tradie=${businessId}`;
  }, [businessId]);

  async function copyLink() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      showToast("Quote link copied.", "success");
    } catch {
      showToast("Could not copy the link.", "error");
    }
  }

  function dismissSample() {
    setSampleDismissed(true);
    try {
      localStorage.setItem(getSampleLeadDismissedStorageKey(businessId), "1");
    } catch {
      /* storage blocked */
    }
  }

  return (
    <div className="rq-overview">
      <section className="rq-overview__hero">
        <div>
          <p className="rq-overview__eyebrow">Workspace setup</p>
          <h1>Get your quote flow ready</h1>
          <p>
            Share your quote link, collect the job details, then quote and
            follow up from the job board.
          </p>
        </div>

        <div className="rq-overview__progress" aria-label="Setup progress">
          <span>{completion.percent}%</span>
          <div>
            <strong>{completion.complete ? "Profile ready" : "Profile needs work"}</strong>
            <small>
              {completion.filled} of {completion.total} key fields done
            </small>
          </div>
        </div>
      </section>

      <div className="rq-overview__actions">
        <button type="button" className="btn" onClick={copyLink} disabled={!link}>
          <Copy size={16} /> Copy quote link
        </button>
        <a className="btn btn-secondary" href={`sms:?body=${encodeURIComponent(`Hi, you can request a quote from me here: ${link}`)}`}>
          <Send size={16} /> Send by SMS
        </a>
        <button type="button" className="btn btn-secondary" onClick={onGoToSettings}>
          <CheckCircle2 size={16} /> Finish setup
        </button>
      </div>

      <HowItWorksPanel
        showSample={requests.length === 0}
        sampleDismissed={sampleDismissed}
        onDismissSample={dismissSample}
      />

      <div className="rq-overview__next">
        <button type="button" onClick={onGoToToday}>
          Open Today <ExternalLink size={15} />
        </button>
        <button type="button" onClick={onGoToPipeline}>
          Open job board <ExternalLink size={15} />
        </button>
      </div>
    </div>
  );
}
